import {
  QrPairResetSummary,
  UserEntity,
  useResetQrPairMutation,
} from 'src/store/userApi';
import { Button } from 'src/ui/Button';
import { Modal } from 'src/ui/Modal';
import { useToast } from 'src/ui/Toast';
import { UserPickerPanel } from 'src/ui/patterns/UserPickerPanel';

export interface QrPairTarget {
  userId: string;
  name: string;
  otherUserId: string;
  otherName: string;
}

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const summaryLine = (counts: QrPairResetSummary) =>
  [
    plural(counts.requests, 'request'),
    plural(counts.suggestions, 'suggestion'),
    plural(counts.conversations, 'conversation'),
  ].join(', ');

interface QrPairResetModalProps {
  target: QrPairTarget | null;
  onClose: () => void;
}

export const QrPairResetModal = ({
  target,
  onClose,
}: QrPairResetModalProps) => {
  const [resetQrPair, { isLoading }] = useResetQrPairMutation();
  const pushToast = useToast();

  const onConfirm = async () => {
    if (!target) return;
    try {
      const counts = await resetQrPair({
        userId: target.userId,
        otherUserId: target.otherUserId,
      }).unwrap();
      pushToast({
        type: 'success',
        title: 'QR pair reset',
        message: `Removed ${summaryLine(counts)}.`,
      });
      onClose();
    } catch {
      pushToast({
        type: 'error',
        title: "Couldn't reset the pair",
        message: 'Nothing was changed. Try again.',
      });
    }
  };

  return (
    <Modal
      open={!!target}
      onClose={isLoading ? undefined : onClose}
      title={
        target
          ? `Reset ${target.name} and ${target.otherName}?`
          : 'Reset QR pair?'
      }
      subtitle="This action cannot be undone."
      footer={
        <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" loading={isLoading} onClick={onConfirm}>
            Reset pair
          </Button>
        </div>
      }
    >
      <ul
        style={{
          margin: 0,
          paddingLeft: 18,
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-secondary)',
        }}
      >
        <li>Every QR request between them, in both directions</li>
        <li>Both match suggestions, whatever created them</li>
        <li>Unmatches them — the same as either one deleting the chat</li>
      </ul>
      <div
        className="text-muted"
        style={{ fontSize: 'var(--fs-xs)', marginTop: 10 }}
      >
        Their next scan starts over as if they had never met.
      </div>
    </Modal>
  );
};

interface QrPairPickerModalProps {
  open: boolean;
  selectedUser: UserEntity;
  onClose: () => void;
  onPick: (target: QrPairTarget) => void;
}

export const QrPairPickerModal = ({
  open,
  selectedUser,
  onClose,
  onPick,
}: QrPairPickerModalProps) => (
  <Modal
    open={open}
    onClose={onClose}
    title="Reset QR with…"
    subtitle={`Pick who to reset ${
      selectedUser.profile?.name ?? 'this user'
    } against.`}
    width={460}
  >
    <div style={{ height: 460, display: 'flex', flexDirection: 'column' }}>
      <UserPickerPanel
        title="Users"
        height={460}
        excludeUserIds={[selectedUser.id]}
        skip={!open}
        onSelect={(user) => {
          if (!user) return;
          onPick({
            userId: selectedUser.id,
            name: selectedUser.profile?.name ?? selectedUser.email,
            otherUserId: user.id,
            otherName: user.profile?.name ?? user.email,
          });
          onClose();
        }}
      />
    </div>
  </Modal>
);
